import { Component, ViewEncapsulation } from '@angular/core';
import { models } from 'powerbi-client';
import { ReportService } from '../../../services/reports/report.service';
import { ReportNavigationService } from '../../../services/report-navigation.service';
import { TabDataService } from '../../../services/tab-data.service';
import {
  ReportEmbedEntry,
  buildReportParamsQueryString,
  populateReportConfigMap,
  findReportEntryById,
  getReportParamsFromUrlOrStorage
} from '../report-embed.utils';

/** One tab in the Reports sub-tab bar (like Inbox/Sent). */
export interface ReportTabItem {
  reportId: string;
  label: string;
  embedConfig: ReportEmbedEntry;
}

@Component({
  selector: 'app-mis-report',
  templateUrl: './mis-report.component.html',
  styleUrls: ['./mis-report.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MisReportComponent {
  reportConfig = {
    type: 'report' as const,
    id: '',
    embedUrl: '',
    accessToken: '',
    tokenType: models.TokenType.Embed,
    hostname: "https://app.powerbi.com",
    settings: {
      panes: {
        filters: { visible: false },
        pageNavigation: { visible: false }
      }
    }
  };
  showSpinner = true;
  /** Sub-tabs inside REPORTS (like Inbox/Sent): Dashboard, then each report. */
  reportTabs: ReportTabItem[] = [];
  /** Index of the selected report sub-tab. */
  selectedReportTabIndex = 0;
  /** Handlers for Power BI embed (dataHyperlinkClicked; buttonClicked handler currently disabled while we use redirect-to-new-tab for buttons). */
  eventHandlers = new Map<string, (event?: { detail?: { url?: string; title?: string; id?: string; type?: string; bookmark?: string } }, _entity?: unknown) => void | null>();

  /** Handler for Power BI embed dataHyperlinkClicked event. */
  private readonly handleDataHyperlinkClicked = (event?: { detail?: { url?: string } }, _entity?: unknown): void => {
    const url = event?.detail?.url;
    if (!url) {
      console.log('[MIS Report] dataHyperlinkClicked: missing url detail');
      return;
    }
    const reportId = this.parseReportIdFromUrl(url);
    if (!reportId) {
      console.log('[MIS Report] dataHyperlinkClicked: could not parse reportId from url', url);
      return;
    }
    const entry = this.findReportEntryById(reportId);
    if (!entry) {
      console.log('[MIS Report] dataHyperlinkClicked: no embed entry for reportId', reportId);
      return;
    }
    const idx = this.ensureReportTab(entry);
    this.selectReportTab(idx);
  };

  private readonly reportConfigByReportId = new Map<string, ReportEmbedEntry>();

  /** SessionStorage key used by auth for Power BI deep link (do not remove here; only read as fallback). */
  private static readonly POWERBI_REPORT_ID_KEY = 'powerbi_reportId';

  /** Resolved rp:* params (from URL or sessionStorage) applied to embedUrl client-side, same as Inquiry reports. */
  private readonly reportParams: Record<string, string | string[]>;

  /** Map dashboard button title (normalized uppercase) to reportId. Extend as more tiles are added; filters can be added later. */
  private static readonly BUTTON_TITLE_TO_REPORT_ID: Record<string, string> = {
    'UNASSIGNED > 1 HR': 'e4aafe4b-033f-492b-ad79-eb5180758127'
  };

  /**
   * Parse reportId from a Power BI report URL.
   * Supports /reports/<guid> and ?reportId=<guid>.
   */
  private parseReportIdFromUrl(url: string): string | null {
    if (!url) return null;
    // Match /reports/<id>
    const pathRegex = /\/reports\/([^/?&#]+)/;
    const pathMatch = pathRegex.exec(url);
    if (pathMatch) return pathMatch[1];
    // Match ?reportId=<id>
    const queryRegex = /[?&]reportId=([^&#]+)/;
    const queryMatch = queryRegex.exec(url);
    if (queryMatch) return queryMatch[1];
    return null;
  }

  constructor(
    private readonly reportService: ReportService,
    private readonly reportNavigationService: ReportNavigationService,
    private readonly tabDataService: TabDataService
  ) {
    // dataHyperlinkClicked: reserved for future in-app navigation — see eventHandlers JSDoc above.
    // this.eventHandlers.set('dataHyperlinkClicked', (event, _entity) => this.onDataHyperlinkClicked(event));
    // buttonClicked: currently disabled; rely on Power BI default redirect/new tab behavior.
    // this.eventHandlers.set('buttonClicked', (event, _entity) => this.onButtonClicked(event));
    // Register the dataHyperlinkClicked handler
    this.eventHandlers.set('dataHyperlinkClicked', this.handleDataHyperlinkClicked);

    const preferredReportId = this.resolvePreferredReportId();
    this.reportParams = this.getReportParamsFromUrlOrStorage() ?? {};
    this.reportService.getPowerBIMisReport(preferredReportId ?? undefined, Object.keys(this.reportParams).length > 0 ? this.reportParams : undefined).subscribe({
      next: reports => {
        this.showSpinner = false;
        if (reports?.results?.length > 0) {
          this.buildReportConfigMap(reports.results);
          this.buildReportTabs(reports.results);
          if (preferredReportId) {
            const entry = this.findReportEntryById(preferredReportId);
            this.reportNavigationService.clearPendingReportId();
            if (entry) {
              const idx = this.ensureReportTab(entry);
              this.selectedReportTabIndex = idx;
              this.applyReportTabConfig(this.reportTabs[idx]);
              if (typeof sessionStorage !== 'undefined') {
                sessionStorage.removeItem(MisReportComponent.POWERBI_REPORT_ID_KEY);
              }
              return;
            }
          }
          const dashboardTab = this.reportTabs.find(t => t.embedConfig.reportName === 'Dashboard');
          if (dashboardTab) {
            this.applyReportTabConfig(dashboardTab);
          }
          if (this.reportTabs.length > 0 && this.selectedReportTabIndex >= this.reportTabs.length) {
            this.selectedReportTabIndex = 0;
          }
        }
      },
      error: error => {
        this.showSpinner = false;
        console.error("Error fetching Power BI MIS report:", error);
      }
    });
  }

  private buildReportConfigMap(results: Array<{ embedReports?: Array<{ reportId?: string; embedUrl?: string; reportName?: string }>; embedToken?: { token?: string } }>): void {
    populateReportConfigMap(this.reportConfigByReportId, results);
  }

  /** Build initial sub-tab list: only Dashboard (or first report if no Dashboard). New tabs added when user opens a report via button/deep link or in-embed link. */
  private buildReportTabs(results: Array<{ embedReports?: Array<{ reportId?: string; embedUrl?: string; reportName?: string }>; embedToken?: { token?: string } }>): void {
    const dashboardItem = results.find((item: { embedReports?: Array<{ reportName?: string }> }) =>
      item.embedReports?.some((r: { reportName?: string }) => r.reportName === 'Dashboard')
    );
    const tabs: ReportTabItem[] = [];
    if (dashboardItem?.embedReports?.length && dashboardItem?.embedToken?.token) {
      const r = dashboardItem.embedReports[0];
      if (r.reportId && r.embedUrl) {
        const entry = this.reportConfigByReportId.get(r.reportId);
        if (entry) {
          tabs.push({
            reportId: entry.reportId,
            label: entry.reportName?.trim() || 'Dashboard',
            embedConfig: entry
          });
        }
      }
    }
    if (tabs.length === 0) {
      const firstEntry = Array.from(this.reportConfigByReportId.values())[0];
      if (firstEntry) {
        tabs.push({
          reportId: firstEntry.reportId,
          label: firstEntry.reportName?.trim() || firstEntry.reportId?.slice(0, 8) || 'Report',
          embedConfig: firstEntry
        });
      }
    }
    this.reportTabs = tabs;
  }

  /** Add a report as a new sub-tab if not already present; returns the tab index. */
  private ensureReportTab(entry: ReportEmbedEntry): number {
    const existing = this.reportTabs.findIndex(t => t.reportId === entry.reportId);
    if (existing >= 0) return existing;
    const tab: ReportTabItem = {
      reportId: entry.reportId,
      label: entry.reportName?.trim() || entry.reportId?.slice(0, 8) || 'Report',
      embedConfig: entry
    };
    this.reportTabs = [...this.reportTabs, tab];
    return this.reportTabs.length - 1;
  }

  /** Apply a report tab's config to the current embed. Same as Inquiry reports: append rp:* client-side. */
  private applyReportTabConfig(tab: ReportTabItem): void {
    this.applyReportTabConfigFromEntry(tab.embedConfig);
  }

  private applyReportTabConfigFromEntry(entry: ReportEmbedEntry): void {
    let embedUrl = entry.embedUrl;
    if (Object.keys(this.reportParams).length > 0) {
      const query = this.buildReportParamsQueryString(this.reportParams);
      if (query) {
        const joinChar = embedUrl.includes('?') ? '&' : '?';
        embedUrl = embedUrl + joinChar + query + '&toolbar=false';
      }
    }
    this.reportConfig = {
      ...this.reportConfig,
      id: entry.reportId,
      embedUrl,
      accessToken: entry.accessToken
    };
  }

  /** Build query string from rp:* params (same format as Inquiry reports). */
  private buildReportParamsQueryString(reportParams: Record<string, string | string[]>): string {
    return buildReportParamsQueryString(reportParams);
  }

  /** Get rp:* params from URL or sessionStorage (same as report-only / Inquiry flow). */
  private getReportParamsFromUrlOrStorage(): Record<string, string | string[]> | undefined {
    return getReportParamsFromUrlOrStorage();
  }

  /** Called when user selects a report sub-tab. */
  selectReportTab(index: number): void {
    if (index < 0 || index >= this.reportTabs.length) return;
    this.selectedReportTabIndex = index;
    this.applyReportTabConfig(this.reportTabs[index]);
  }

  /** Resolve deep-link report id: service first, then sessionStorage, then URL (so it works even if timing clears the service). */
  private resolvePreferredReportId(): string | null {
    const fromService = this.reportNavigationService.getPendingReportId();
    if (fromService) return fromService.trim() || null;
    if (typeof sessionStorage === 'undefined') return null;
    const fromStorage = sessionStorage.getItem(MisReportComponent.POWERBI_REPORT_ID_KEY);
    if (fromStorage) return fromStorage.trim() || null;
    const loc = (globalThis as any).location;
    const search = typeof loc?.search === 'string' ? loc.search : '';
    const hash = typeof loc?.hash === 'string' ? loc.hash : '';
    const reportIdRe = /[?&]reportId=([^&]+)/;
    let m = reportIdRe.exec(search);
    if (!m && hash) m = reportIdRe.exec(hash);
    return m?.[1] ? decodeURIComponent(m[1]).trim() : null;
  }

  /** Find report in map by id (exact, normalized, embedUrl contains, then typo: requested id with one trailing char removed). */
  private findReportEntryById(reportId: string): ReportEmbedEntry | undefined {
    return findReportEntryById(this.reportConfigByReportId, reportId);
  }

  /** Handle Power BI report button click: map title to reportId and open that report in a new Reports sub-tab. */
  private onButtonClicked(event?: { detail?: { title?: string; id?: string; type?: string; bookmark?: string } }): void {
    console.log('[MIS Report] buttonClicked event', event);
    const title = event?.detail?.title?.trim();
    if (!title) {
      console.log('[MIS Report] buttonClicked: missing title in event detail');
      return;
    }
    const normalizedTitle = title.toUpperCase();
    const reportId = MisReportComponent.BUTTON_TITLE_TO_REPORT_ID[normalizedTitle];
    console.log('[MIS Report] buttonClicked: normalizedTitle', normalizedTitle, 'mapped reportId', reportId);
    if (!reportId) {
      console.log('[MIS Report] buttonClicked: no mapping for title', normalizedTitle);
      return;
    }

    // For button-triggered navigation, explicitly ask backend for this report's embed config
    // (similar to deep-link flow) so that the correct paginated report and dataset are used.
    this.showSpinner = true;
    this.reportService.getPowerBIMisReport(reportId).subscribe({
      next: reports => {
        this.showSpinner = false;
        if (!reports?.results?.length) {
          console.log('[MIS Report] buttonClicked: backend returned no results for reportId', reportId);
          return;
        }
        // Refresh the local map from backend response and then open the specific report.
        this.buildReportConfigMap(reports.results);
        const entry = this.findReportEntryById(reportId);
        console.log('[MIS Report] buttonClicked: entry found after backend call?', !!entry);
        if (!entry) {
          console.log('[MIS Report] buttonClicked: no embed entry for mapped reportId after backend call', reportId);
          return;
        }
        const idx = this.ensureReportTab(entry);
        console.log('[MIS Report] buttonClicked: opening sub-tab index', idx, 'for reportId', reportId);
        this.selectReportTab(idx);
      },
      error: err => {
        this.showSpinner = false;
        console.error('[MIS Report] buttonClicked: getPowerBIMisReport failed for reportId', reportId, err);
      }
    });
  }

}